import { useContext, useEffect, useState } from 'react';
import { useNavigation } from '../../../utils/redirectUtils';
import { SourceContext } from '../../../utils/sourceContext';
import { getCachedState, initUserState } from '../../../utils/stateContext';
import Header from '../../.shared/header/header';
import Footer from '../../.shared/footer/footer';
import PatientStories from '../../.shared/PatientStories/PatientStories';
import FAQ from '../../.shared/FAQ/FAQ';
import AsSeenOn from '../../.shared/AsSeenOn/AsSeenOn';
import HowItWorks from '../../.shared/HowItWorks/HowItWorks';
import WhatsIncluded from '../../.shared/WhatsIncluded/WhatsIncluded';
import PriceUpdateBanner from '../../Landing/PriceUpdateBanner/PriceUpdateBanner';
import Pricing from '../../../data/Pricing';
import { trackPostHogEvent } from '../../../utils/posthogUtils';
import { startInterview } from '../../../utils/interviewFlow';
import CyclingText from '../../Landing/CyclingText';
import styles from './BaseLandingV4.module.css';

const HERO_BULLETS = [
    'One price, all doses — your bill never climbs with your dose',
    'No telehealth visit required for most prescriptions',
    'A licensed clinician reviews every plan, then checks in monthly',
];

// The control Landing's hero copy (OriginalHero). The /benefithub test uses it
// so that only this layout and image differ from control, not the words.
const CONTROL_HERO_PHRASES = [
    'Weight Loss',
    'Body Transformation',
    'Metabolic Reset',
    'Lifestyle Change',
    'Wellness Revolution',
];

const CONTROL_HERO_BULLETS = [
    'One Price, All Doses',
    'No telehealth visit required for most prescriptions',
    'Real people, like you, are losing 15-26% of their body weight',
];

export default function BaseLandingV4({ controlHeroCopy = false }) {
    const { safeSource, switchAndSave } = useContext(SourceContext);
    const navigate = useNavigation();

    // Same rule as Landing: no price banner for visitors in states without
    // switch-and-save pricing, and none until their state is known.
    const [userState, setUserState] = useState(getCachedState);
    const NON_SWITCH_AND_SAVE_STATES = (() => { try { return JSON.parse(process.env.REACT_APP_NON_SWITCH_AND_SAVE_STATES || '[]'); } catch { return []; } })();
    const checksState = NON_SWITCH_AND_SAVE_STATES.length > 0;
    const showPriceBanner = switchAndSave && (!checksState || (userState !== null && !NON_SWITCH_AND_SAVE_STATES.includes(userState)));

    useEffect(() => {
        if (checksState) {
            initUserState().then(() => setUserState(getCachedState()));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const pricing = Pricing();
    const semaglutide = pricing.getPricingOptionsByProductName('semaglutide');
    const startingPrice = semaglutide?.injectable?.threeMonths?.monthly_amount?.split('.')[0] ?? null;

    const handleCta = (selected, path) => {
        // Only the funnel CTA enrolls; the other destinations are untouched.
        const flow = path === '/interview' ? startInterview() : null;

        trackPostHogEvent('landing_page_submitted', {
            selected,
            to_path: path,
            interview_flow: flow?.id
        });

        navigate(flow ? flow.entryPath : path);
    };

    return (
        <div className={styles.page}>
            {showPriceBanner && <PriceUpdateBanner />}

            <Header hideNavigation transparent />

            <section className={styles.heroB}>
                <div className={styles.heroBArt} aria-hidden="true"></div>

                <div className={styles.heroBInner}>
                    <div className={styles.heroBCopy}>
                        <div className={`${styles.heroBPill} ${showPriceBanner ? styles.heroBPillWithBanner : ''}`}>
                            {controlHeroCopy
                                ? (safeSource ? 'Guided Medical Weight Loss' : 'Safe Effective Proven GLP-1')
                                : 'Safe · Effective · Proven GLP-1'}
                        </div>
                        {controlHeroCopy ? (
                            <h1 className={styles.heroBTitle}>
                                <CyclingText phrases={CONTROL_HERO_PHRASES} className={`${styles.heroBGrad} ${styles.heroBCycle}`} />
                                <span className={styles.heroBJourney}>Your Journey Starts Here</span>
                            </h1>
                        ) : (
                            <>
                                <h1 className={styles.heroBTitle}>
                                    Your <span className={styles.heroBGrad}>metabolic reset</span> starts here.
                                </h1>
                                <p className={styles.heroBSub}>
                                    Clinician-guided semaglutide and tirzepatide, delivered to your door.
                                    Real people like you are losing 15–26% of their body weight.
                                </p>
                            </>
                        )}
                        <ul className={styles.heroBList}>
                            {(controlHeroCopy ? CONTROL_HERO_BULLETS : HERO_BULLETS).map((bullet) => (
                                <li key={bullet}>
                                    <span className={styles.heroBCheck} aria-hidden="true">
                                        <svg viewBox="0 0 24 24" fill="none">
                                            <polyline points="5 12.5 10 17.5 19 7.5" stroke="white" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                    <span>{bullet}</span>
                                </li>
                            ))}
                        </ul>
                        <div className={styles.heroBActions}>
                            <button type="button" className={styles.heroBCta} onClick={() => handleCta('get_started', '/interview')}>
                                Get Started
                            </button>
                            {startingPrice && !controlHeroCopy && (
                                <div className={styles.heroBPrice}>
                                    Starting at <span className={styles.heroBPriceAmt}>${startingPrice}</span>/month
                                    <span className={styles.heroBPriceFine}>Cancel anytime · No insurance needed</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <AsSeenOn />

            <HowItWorks />

            <WhatsIncluded />

            <PatientStories />

            <FAQ />

            <Footer />
        </div>
    );
}
